import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { agentWidgetsApiBaseUrl } from '../../api/server';
import { loadSharedWidgetUI } from './sharedWidgetUI';

export default function TestChatModal({ context, target, isNewAgent = false, isOpen = true, onClose, onOpen, onNewTest, onActivityChange }) {
  const host = useRef(null);
  const closeButton = useRef(null);
  const lastWidgetFocus = useRef(null);
  const [error, setError] = useState('');
  const [active, setActive] = useState(false);
  const activityCallback = useRef(onActivityChange);
  activityCallback.current = onActivityChange;
  const [loading, setLoading] = useState(!isNewAgent);
  const targetKey = JSON.stringify(target);
  useEffect(() => {
    if (isNewAgent) return;
    let cancelled = false;
    let mounted;
    setLoading(true); setError('');
    loadSharedWidgetUI().then(widget => {
      if (cancelled) return;
      const snapshot = JSON.parse(targetKey);
      const adapter = widget.createDashboardSessionAdapter({
        baseUrl: agentWidgetsApiBaseUrl, context: { client_id: context.client_id, email: context.email },
        agentId: snapshot.agent_id,
        ...(snapshot.version != null ? { version: snapshot.version } : {}),
        ...(snapshot.draft_configuration != null ? { draftConfiguration: snapshot.draft_configuration } : {}),
      });
      mounted = widget.mountTestChat(host.current, { adapter, title: 'Test Chat', onActiveChange: value => { setActive(value); activityCallback.current?.(value); } });
      setLoading(false);
    }).catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; mounted?.unmount(); activityCallback.current?.(false); };
  }, [context.client_id, context.email, targetKey, isNewAgent]);
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    (lastWidgetFocus.current?.isConnected ? lastWidgetFocus.current : closeButton.current)?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus?.(); };
  }, [isOpen]);
  const rememberWidgetFocus = () => {
    let focused = document.activeElement;
    while (focused?.shadowRoot?.activeElement) focused = focused.shadowRoot.activeElement;
    if (host.current?.shadowRoot?.contains(focused)) lastWidgetFocus.current = focused;
  };
  const minimize = () => { rememberWidgetFocus(); onClose(); };
  const keys = event => {
    if (event.key === 'Escape') { event.stopPropagation(); minimize(); }
    if (event.key !== 'Tab') return;
    const controls = [];
    const collect = root => {
      for (const element of root.querySelectorAll('*')) {
        if (element.matches('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href], [tabindex="0"]') && element.getClientRects().length) controls.push(element);
        if (element.shadowRoot) collect(element.shadowRoot);
      }
    };
    collect(event.currentTarget);
    let focused = document.activeElement;
    while (focused?.shadowRoot?.activeElement) focused = focused.shadowRoot.activeElement;
    const first = controls[0]; const last = controls[controls.length - 1];
    if (event.shiftKey && focused === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && focused === last) { event.preventDefault(); first?.focus(); }
  };
  return <>
    {!isOpen && !active && <button type="button" className="fixed bottom-5 right-5 z-40 px-4 py-3 bg-indigo-600 text-white rounded-lg shadow-lg text-sm" onClick={onOpen}>Resume Test Chat</button>}
    <div hidden={!isOpen} className={`fixed inset-0 z-50 bg-black bg-opacity-40 ${isOpen ? 'flex' : 'hidden'} items-center justify-center p-4 whitespace-normal`} onKeyDown={keys}>
      <div role="dialog" aria-modal="true" aria-labelledby="test-chat-title" className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b p-4"><h2 id="test-chat-title" className="text-lg font-semibold">Test Chat</h2><button ref={closeButton} type="button" onMouseDown={rememberWidgetFocus} onClick={minimize} aria-label="Minimize Test Chat" className="p-2 rounded hover:bg-gray-100"><X size={18} /></button></div>
        <div className="px-4 py-2 text-xs text-gray-500">{target.agent_id} · {target.draft_configuration ? 'Unsaved settings' : `Version ${target.version || 'latest'}`}</div>
        {isNewAgent ? <p className="p-4 text-sm" role="status">Save this agent before starting Test Chat.</p> : <>
          {loading && <p className="p-4 text-sm" role="status">Loading chat widget…</p>}
          {error && <p className="p-4 text-sm text-red-700" role="alert">{error}</p>}
          <div ref={host} style={{ height: 'min(640px, 75vh)', width: '100%' }} />
          {onNewTest && <button type="button" disabled={active} className="m-3 text-xs text-indigo-600 disabled:text-gray-400 disabled:cursor-not-allowed" onClick={onNewTest}>Test latest saved or draft settings</button>}
        </>}
      </div>
    </div>
  </>;
}
